// src/services/serviceService.ts
import firestore from '@react-native-firebase/firestore';
import {
  ServiceSchedule,
  ServiceRecord,
  CreateServiceScheduleData,
  CreateServiceRecordData,
  ServiceSummaryByTruck,
  computeDueDateStatus,
} from '../types/Service';
import { ErrorLogger } from '../utils/errorLogger';
import { mapServiceScheduleDoc, mapServiceRecordDoc } from '../utils/mappers';

const SERVICES_COLLECTION = 'services';
const RECORDS_COLLECTION = 'serviceRecords';

export class ServiceService {
  private static servicesCol() {
    return firestore().collection(SERVICES_COLLECTION);
  }

  private static recordsCol() {
    return firestore().collection(RECORDS_COLLECTION);
  }

  static async createSchedule(
    userId: string,
    data: CreateServiceScheduleData,
  ): Promise<ServiceSchedule> {
    try {
      const now = new Date();
      const doc: Omit<ServiceSchedule, 'id'> = {
        userId,
        truckId: data.truckId,
        truckPlateNumber: data.truckPlateNumber,
        serviceType: data.serviceType,
        isCustomType: data.isCustomType,
        intervalDays: data.intervalDays,
        lifecycleStatus: 'active',
        nextDueDate: data.nextDueDate,
        createdAt: now,
        updatedAt: now,
      };
      const ref = await this.servicesCol().add(doc);
      return { id: ref.id, ...doc };
    } catch (error) {
      ErrorLogger.logError(error, 'ServiceService.createSchedule');
      throw error;
    }
  }

  static async getSchedulesByTruck(
    truckId: string,
  ): Promise<ServiceSchedule[]> {
    try {
      const snapshot = await this.servicesCol()
        .where('truckId', '==', truckId)
        .get();
      return snapshot.docs
        .map(doc => mapServiceScheduleDoc(doc))
        .sort((a, b) => a.nextDueDate.getTime() - b.nextDueDate.getTime());
    } catch (error) {
      ErrorLogger.logError(error, 'ServiceService.getSchedulesByTruck');
      return [];
    }
  }

  static async updateLifecycleStatus(
    scheduleId: string,
    lifecycleStatus: 'active' | 'paused',
  ): Promise<void> {
    try {
      await this.servicesCol()
        .doc(scheduleId)
        .update({ lifecycleStatus, updatedAt: new Date() });
    } catch (error) {
      ErrorLogger.logError(error, 'ServiceService.updateLifecycleStatus');
      throw error;
    }
  }

  static async deleteSchedule(scheduleId: string): Promise<void> {
    try {
      // Query records first, then batch-delete records + schedule atomically
      const snapshot = await this.recordsCol()
        .where('serviceScheduleId', '==', scheduleId)
        .get();
      const batch = firestore().batch();
      snapshot.docs.forEach(doc => batch.delete(doc.ref));
      batch.delete(this.servicesCol().doc(scheduleId));
      await batch.commit();
    } catch (error) {
      ErrorLogger.logError(error, 'ServiceService.deleteSchedule');
      throw error;
    }
  }

  static async recordService(
    userId: string,
    data: CreateServiceRecordData,
  ): Promise<ServiceRecord> {
    try {
      const now = new Date();
      const msPerDay = 1000 * 60 * 60 * 24;
      const daysEarly = Math.floor(
        (data.currentNextDueDate.getTime() - data.performedDate.getTime()) / msPerDay,
      );
      const wasUnexpected =
        data.userMarkedUnexpected || daysEarly > data.intervalDays * 0.1;

      const record: Omit<ServiceRecord, 'id'> = {
        userId,
        truckId: data.truckId,
        serviceScheduleId: data.serviceScheduleId,
        serviceType: data.serviceType,
        performedDate: data.performedDate,
        cost: data.cost,
        notes: data.notes,
        wasUnexpected,
        userMarkedUnexpected: data.userMarkedUnexpected,
        createdAt: now,
      };

      const batch = firestore().batch();
      const recordRef = this.recordsCol().doc(); // auto-generate ID
      batch.set(recordRef, record);
      batch.update(this.servicesCol().doc(data.serviceScheduleId), {
        nextDueDate: data.newNextDueDate,
        lifecycleStatus: 'active',
        updatedAt: now,
      });
      await batch.commit();

      return { id: recordRef.id, ...record };
    } catch (error) {
      ErrorLogger.logError(error, 'ServiceService.recordService');
      throw error;
    }
  }

  static async getRecordsBySchedule(scheduleId: string): Promise<ServiceRecord[]> {
    try {
      const snapshot = await this.recordsCol()
        .where('serviceScheduleId', '==', scheduleId)
        .get();
      return snapshot.docs
        .map(doc => mapServiceRecordDoc(doc))
        .sort((a, b) => b.performedDate.getTime() - a.performedDate.getTime());
    } catch (error) {
      ErrorLogger.logError(error, 'ServiceService.getRecordsBySchedule');
      return [];
    }
  }

  static async getOverdueSummaryByTruck(): Promise<ServiceSummaryByTruck> {
    try {
      // Fleet-wide: grouping and status are computed client-side to avoid
      // composite indexes (consistent with the other services).
      const snapshot = await this.servicesCol().get();

      const summary: ServiceSummaryByTruck = {};
      snapshot.docs.forEach(doc => {
        const schedule: ServiceSchedule = mapServiceScheduleDoc(doc);
        const status = computeDueDateStatus(schedule);
        if (status !== 'overdue' && status !== 'due-soon') return;
        if (!summary[schedule.truckId]) {
          summary[schedule.truckId] = { overdue: 0, dueSoon: 0 };
        }
        if (status === 'overdue') summary[schedule.truckId].overdue++;
        else summary[schedule.truckId].dueSoon++;
      });

      return summary;
    } catch (error) {
      ErrorLogger.logError(error, 'ServiceService.getOverdueSummaryByTruck');
      return {};
    }
  }
}

export default ServiceService;
